import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FC, FormEvent, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import styled from 'styled-components';

import Button from '@/components/Button';
import { FormLayout, Input, Label, Larger, Textarea } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import { RemoveButton } from '@/components/Testing/index';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import useOnCloseWithConfirmation from '@/hooks/useOnCloseWithConfirmation';
import ChannelsList from '@/pages/DistributionSchedule/components/ChannelsList';
import FormChannelGroupSwitch from '@/pages/DistributionSchedule/components/FormChannelGroupSwitch';
import {
  ChannelGroupResource,
  ChannelResource,
  ReleaseResource,
} from '@/types/resources';
import { api } from '@/utils/api';
import { ReleaseCompilationTypeEnum } from '@/utils/enums';
import { handleFormErrors } from '@/utils/helpers';
import { hexToRgba } from '@/utils/theme';

const compilationTypes = [
  {
    value: ReleaseCompilationTypeEnum.FRESH,
    label: 'Fresh',
  },
  {
    value: ReleaseCompilationTypeEnum.DISTRIBUTION,
    label: 'Distibution',
  },
  {
    value: ReleaseCompilationTypeEnum.MODIFIED_REDISTRIBUTION,
    label: 'Modified Distribution',
  },
  {
    value: ReleaseCompilationTypeEnum.REUSE,
    label: 'Reuse',
  },
  {
    value: ReleaseCompilationTypeEnum.REDISTRIBUTION,
    label: 'Redistribution',
  },
  {
    value: ReleaseCompilationTypeEnum.REDISTRIBUTION_PREDICTED,
    label: 'Redistribution / Predicted',
  },
];

const RepostBox = styled.div`
  background: ${({ theme }) => hexToRgba(theme.colors.grayscaleLight, 0.5)};
  padding: 36px 32px 32px;
  margin: 0 -32px;
  position: relative;
`;

const GroupRepostBox = styled(RepostBox)`
  margin-bottom: 32px;
`;

export const AddButtonHolder = styled.div`
  margin-bottom: 24px;
  svg {
    margin-right: 8px;
  }

  button {
    width: 100%;
  }
`;

const StyledRemoveButton = styled(RemoveButton)`
  position: absolute;
  right: 32px;
  top: 24px;
`;

const FormDescription = styled.div`
  margin-bottom: 16px;
`;

const FormDescriptionBox = styled.div`
  display: flex;
  align-items: center;
  padding: 8px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 4px;
  margin-bottom: 32px;
  ${Text} {
    flex: 1;
  }
`;

const IconWrap = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  background: ${({ theme }) => theme.colors.grayscaleLight};
  color: ${({ theme }) => theme.colors.black};
`;

const StyledSidePanelContent = styled(SidePanelContent)`
  padding: 24px 32px 56px;
`;

const StyledLarger = styled(Larger)`
  margin-top: -24px;
`;

interface Target {
  channel_id?: number;
  released_at: string;
}

type FormDataT = {
  compilation_type?: number;
  targets: Target[];
  channel_group?: number;
  released_at: string;
};

interface Props {
  onClose: () => void;
  release: Pick<
    ReleaseResource,
    'id' | 'projectId' | 'description' | 'title' | 'freshId'
  >;
}

const DistributionRescheduleModal: FC<Props> = ({ onClose, release }) => {
  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const [isChannelGroups, setIsChannelGroups] = useState(false);

  const { data: channelOptions } = useQuery(
    ['channels', { onlyBp: true, authorized: true }],
    () =>
      api.get<never, ChannelResource[]>(`/v1/channels/authorized`, {
        params: {
          // ...(teamId !== "all" && {
          //   team_id: teamId,
          // }),
          only_bp: 1,
        },
      }),
    {
      staleTime: 1000 * 60,
      select: (data) =>
        data.map((channel) => ({ label: channel.name, value: channel.id })),
    },
  );

  const { data: channelGroups } = useQuery(
    ['channel_groups'],
    () =>
      api.get<never, ChannelGroupResource[]>(`/v1/distribution/channel-groups`),
    {
      staleTime: 1000 * 60,
    },
  );

  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    watch,
    formState: { isDirty, isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      targets: [
        {
          released_at: '',
        },
      ],
    },
  });

  const closeDialog = useOnCloseWithConfirmation(onClose, isDirty);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'targets',
  });

  const channelGroup = watch('channel_group');

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        const newTargets = channelGroups
          ?.find((group) => group.id === channelGroup)
          ?.channels.map((channel) => {
            return {
              channel_id: channel.id,
              released_at: data.released_at,
            };
          });

        await api.post<FormDataT, { success: boolean }>(
          `/v1/releases/${release.id}/reschedule`,
          {
            ...data,
            is_channel_group: isChannelGroups,
            ...(isChannelGroups && { targets: newTargets }),
          },
        );

        queryClient.invalidateQueries(['releases']);

        alerts.success(`New release has been successfully created`);
        onClose();
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true);
      }
    })(e);
  };

  return (
    <Modal>
      <SidePanel heading="New schedule" onClose={closeDialog}>
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <StyledSidePanelContent>
            <FormDescription>
              <Label>Video & Thumb</Label>
              <FormDescriptionBox>
                <IconWrap>
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M16.2843 16.285H21.4272V2.57068H7.71289V7.71354"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M16.2846 7.71375H2.57031V21.428H16.2846V7.71375Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </IconWrap>
                <Text $color="gray" $type="smallMedium">
                  Video and thumbnail files will be used as a duplicate of the
                  original file.
                </Text>
              </FormDescriptionBox>
              <FormChannelGroupSwitch
                isChannelGroups={isChannelGroups}
                setIsChannelGroups={setIsChannelGroups}
              />
            </FormDescription>
            {isChannelGroups ? (
              <GroupRepostBox>
                <FormLayout>
                  <FormField<FormDataT, number>
                    control={control}
                    type="select"
                    name="channel_group"
                    label="Channel Groups"
                    options={
                      channelGroups?.map((group) => {
                        return {
                          label: group.name,
                          value: group.id,
                        };
                      }) ?? []
                    }
                  />
                  {!!channelGroup && !!channelGroups?.length && (
                    <StyledLarger>
                      <ChannelsList
                        isWhite
                        currentGroup={channelGroups.find(
                          (group) => group.id === channelGroup,
                        )}
                      />
                    </StyledLarger>
                  )}
                  <FormField<FormDataT>
                    type="datepicker"
                    control={control}
                    name="released_at"
                    label="Date & Time"
                  />
                </FormLayout>
              </GroupRepostBox>
            ) : (
              <FormLayout>
                {fields.map((item, index) => (
                  <Larger key={item.id}>
                    <RepostBox>
                      {fields.length > 1 && (
                        <StyledRemoveButton
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            remove(index);
                          }}
                        >
                          Remove
                        </StyledRemoveButton>
                      )}
                      <FormLayout>
                        <FormField<FormDataT, number>
                          options={channelOptions ?? []}
                          type="select"
                          control={control}
                          name={`targets.${index}.channel_id`}
                          label={`Target Channel (${index + 1})`}
                        />
                        <FormField<FormDataT>
                          type="datepicker"
                          control={control}
                          name={`targets.${index}.released_at`}
                          label="Date & Time"
                        />
                      </FormLayout>
                    </RepostBox>
                  </Larger>
                ))}
                <Larger>
                  <AddButtonHolder>
                    <Button
                      type="button"
                      $type="colorized"
                      onClick={() => append({ released_at: '' })}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8 2V14"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M2 8H14"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Add
                    </Button>
                  </AddButtonHolder>
                </Larger>
              </FormLayout>
            )}
            <FormLayout>
              <Larger>
                <Label htmlFor="titleInput">Title</Label>
                <Input
                  id="titleInput"
                  readOnly
                  disabled
                  value={release.title}
                />
              </Larger>
              <Larger>
                <Label htmlFor="descriptionInput">Description</Label>
                <Textarea
                  id="descriptionInput"
                  disabled
                  readOnly
                  value={release.description}
                />
              </Larger>
              <Larger>
                <Label htmlFor="projectIdInput">Project ID</Label>
                <Input
                  id="projectIdInput"
                  readOnly
                  disabled
                  value={release.projectId}
                />
              </Larger>
              <Larger>
                <Label htmlFor="freshIdInput">Fresh ID</Label>
                <Input
                  id="freshIdInput"
                  readOnly
                  disabled
                  value={release.freshId}
                />
              </Larger>
              <FormField<FormDataT, ReleaseCompilationTypeEnum>
                options={compilationTypes}
                type="select"
                control={control}
                name="compilation_type"
                label="Compilation type"
                menuPlacement="top"
              />
            </FormLayout>
          </StyledSidePanelContent>
          <SidePanelFooter $actions>
            <Button disabled={isSubmitting} loading={isSubmitting}>
              Schedule
            </Button>
            <Button color="secondary" onClick={closeDialog} type="button">
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default DistributionRescheduleModal;
